import { nextTick } from "vue";

/**
 * 겹쳐 뜨는 층(모달 · 사이드바 드로어) 공용 도우미
 * - `AppModal.vue` 와 `DefaultLayout.vue` 의 드로어가 함께 쓴다. 둘이 따로 body 스크롤을 잠그고 풀면, 모달 위에 확인창이 뜨고 닫힐 때
 *   먼저 닫힌 쪽이 잠금을 풀어 뒤 화면이 스크롤됐다. 그래서 잠금은 개수(카운터)로 센다.
 */

let lockCount = 0;
let savedOverflow = "";
let savedPaddingRight = "";

/**
 * body 스크롤을 잠그고, 푸는 함수를 돌려준다. 여러 층이 겹치면 마지막 층이 풀릴 때 원래 값으로 되돌린다.
 * 스크롤 막대가 사라지며 본문이 옆으로 밀리지 않도록 막대 폭만큼 오른쪽 패딩을 채운다(막대가 겹쳐 그려지는 macOS 에서는 0).
 */
export function lockBodyScroll(): () => void {
  const { body, documentElement } = document;
  if (lockCount === 0) {
    savedOverflow = body.style.overflow;
    savedPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    body.style.overflow = "hidden";
  }
  lockCount += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      body.style.overflow = savedOverflow;
      body.style.paddingRight = savedPaddingRight;
    }
  };
}

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  // 접힘(`details > summary`)도 Tab 으로 닿는다 — 빠지면 층의 첫 · 끝 요소 계산이 어긋나 접힘 줄이 첫 버튼 앞에 있을 때 Shift+Tab 이 건너뛴다(90차 C90-26 확인)
  "summary",
  "[tabindex]:not([tabindex='-1'])",
]
  // tabindex="-1" 은 Tab 순서에서 빠진 요소(로빙 탭의 비선택 탭 등)라 첫 · 마지막 요소 계산에서도 뺀다.
  .map((selector) => `${selector}:not([tabindex='-1'])`)
  .join(",");

/** 컨테이너 안에서 키보드로 닿을 수 있는(보이는) 요소 목록 */
export function focusableIn(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el.getClientRects().length > 0,
  );
}

/**
 * Tab · Shift+Tab 이 층 밖으로 나가지 않게 첫 요소와 마지막 요소 사이에서 돈다. 처리했으면 true.
 * 포커스가 층 밖에 있으면(배경 클릭 등) 첫 요소로 되돌린다.
 */
export function trapTabKey(
  event: KeyboardEvent,
  container: HTMLElement,
): boolean {
  if (event.key !== "Tab") return false;
  const items = focusableIn(container);
  const first = items[0];
  const last = items.at(-1);
  if (!first || !last) {
    event.preventDefault();
    container.focus();
    return true;
  }
  const active = document.activeElement;
  const onEdge =
    !(active instanceof Node) ||
    !container.contains(active) ||
    active === container ||
    active === (event.shiftKey ? first : last);
  if (!onEdge) return false;
  event.preventDefault();
  (event.shiftKey ? last : first).focus();
  return true;
}

/**
 * 열린 모달 순서(맨 뒤가 맨 위). ESC · Tab 은 맨 위 모달만 처리한다 — 입력형 모달 위에 확인창이 떴을 때 ESC 한 번에 둘 다 닫히지 않게.
 */
const modalStack: symbol[] = [];

/**
 * 모달이 떠 있는 동안 막을 배경(74차 UXV74-06) — 레이아웃 루트(DefaultLayout · AuthLayout · ErrorLayout 의 `data-ui-layout-root`)와
 * 레이아웃 없는 공개 화면의 셸. `aria-modal` 만으로는 iOS VoiceOver 쓸기 탐색이 뒤 화면 표 · 버튼으로 나갔다.
 * 모달 · 시트 · Toaster · SessionNotice · AI 도우미 패널은 `#app` 직계 형제(Teleport)라 걸리지 않는다. 비모달 SidePanel 은
 * `pushModal` 을 부르지 않으므로 본문이 그대로 살아 있다.
 * - 비모달 SidePanel · AI 도우미(둘 다 `#app` 직계 — 레이아웃 루트 밖)는 모달 중에도 쓸기 탐색에 남는다. 누름은 배경막이 막는다.
 *   74차 C-10 범위에서 일부러 걸지 않았다 — 막아야 하면 여기 `INERT_ROOTS` 에 더한다.
 */
const INERT_ROOTS = "[data-ui-layout-root], #app > .ui-public-shell";
let inertRoots: HTMLElement[] = [];

/**
 * 배경을 `inert` 로. 원래 inert 였던 요소는 건드리지 않는다(풀 때도 그대로 둔다). 이미 건 요소는 다시 세지 않으므로 여러 번 불러도 된다.
 */
function inertBackground(): void {
  const added = [...document.querySelectorAll<HTMLElement>(INERT_ROOTS)].filter(
    (el) => !el.inert && !inertRoots.includes(el),
  );
  for (const el of added) el.inert = true;
  inertRoots.push(...added);
}

/** 마지막 모달이 닫힐 때 푼다 — 닫힌 뒤 연 요소로 포커스를 되돌리기 전에 풀려야 하므로 `removeModal` 안에서 바로 부른다. */
function releaseBackground(): void {
  for (const el of inertRoots) el.inert = false;
  inertRoots = [];
}

export function pushModal(): symbol {
  const id = Symbol("modal");
  if (modalStack.length === 0) inertBackground();
  modalStack.push(id);
  // 라우트 진입과 같은 패치에서 열린 채 마운트되는 모달은 `setup` 때 레이아웃 루트가 아직 문서에 없다(74차 fix1 L6) — 패치가
  // 끝난 뒤(이번 렌더의 onMounted 와 같은 때) 다시 건다. 그 사이 닫혔으면 걸지 않는다.
  void nextTick(() => {
    if (modalStack.length > 0) inertBackground();
  });
  return id;
}

export function removeModal(id: symbol): void {
  const index = modalStack.indexOf(id);
  if (index < 0) return;
  modalStack.splice(index, 1);
  if (modalStack.length === 0) releaseBackground();
}

export function isTopModal(id: symbol): boolean {
  return modalStack.at(-1) === id;
}

/** 열린 모달이 하나라도 있는지. 드로어처럼 모달 아래에 깔린 층은 이때 키보드(ESC · Tab)를 처리하지 않는다. */
export function hasOpenModal(): boolean {
  return modalStack.length > 0;
}
